<template>
  <component
    :is="wrapperComponent"
    class="sp-quantum-select"
    :class="classModifiers"
    :open="menuOpen"
    :no-restore-focus="props.noRestoreFocus"
    :close-on-content-click="closeOnContentClick"
    :disabled="menuDisabled"
    :pointer="pointer"
    :mobile-expand-height-max="mobileExpandHeightMax"
    :no-sheet="keepsPopup"
    :location="location"
    :permanent="permanent"
    @update-open="handleMenuUpdateOpen"
    @open="handleMenuShown"
    @close="resetWhenClosed"
  >
    <div v-if="!noMenu" slot="activator" class="sp-quantum-select__activator" :class="fieldModifiers">
      <div v-if="selectedTagsEnabled" class="sp-quantum-select__selected-content" data-activator-control>
        <sp-chip
          v-for="tag in tags"
          :key="tag.value"
          class="sp-quantum-select__tag"
          data-selection-control
          :label="tag.label"
          :closable="canRemove"
          @close="tag.onClose"
        ></sp-chip>
        <div class="sp-quantum-select__selected-input" data-activator-control>
          <slot ref="activatorSlot" name="activator" class="sp-quantum-select__activator-slot">
            <sp-button :disabled="disabled">Open menu</sp-button>
          </slot>
        </div>
      </div>
      <div v-else-if="hasSelectionControls" class="sp-quantum-select__selected-input" data-activator-control>
        <slot ref="activatorSlot" name="activator" class="sp-quantum-select__activator-slot">
          <sp-button :disabled="disabled">Open menu</sp-button>
        </slot>
      </div>
      <slot v-else ref="activatorSlot" name="activator" class="sp-quantum-select__activator-slot">
        <sp-button :disabled="disabled">Open menu</sp-button>
      </slot>
      <div v-if="hasSelectionControls" v-show="hasSelectionActions" class="sp-quantum-select__selected-actions">
        <sp-button
          v-if="showClear"
          class="sp-quantum-select__clear"
          data-selection-control
          size="small"
          color="surface"
          icon="close-filled"
          :disabled="!canClear"
          :accessible-name="clearLabel"
          @mousedown.prevent
          @click.stop="clearSelection"
        ></sp-button>
        <slot ref="selectionTrailingSlot" name="selection-trailing"></slot>
      </div>
    </div>
    <div class="sp-quantum-select__content">
      <NavigationContainerShell
        :disabled="disabled"
        :max-height="computedMaxHeight"
        :no-sheet="keepsPopup"
        :appearance="props.appearance"
        :title="attrs.title"
        :hide-title-bar="props.hideTitleBar"
        :on-back="containerOnBack"
      >
        <template #before-content>
          <slot name="before-content" />
        </template>
        <template #navbar-actions>
          <slot name="navbar-actions" />
        </template>
        <component
          :is="containerComponent"
          :key="containerResetKey"
          :disabled="disabled"
          :list-label="attrs.ariaLabel"
          :item-title="props.itemTitle"
          :item-value="props.itemValue"
          :item-subtitle="props.itemSubtitle"
          :item-tags="props.itemTags"
          :item-test-id="props.itemTestId"
          :input-test-id="props.inputTestId"
          :clear-test-id="props.clearTestId"
          :clearable="clearable"
          :listen-on-key-event="listenOnKeyEvent"
          :selected="model"
          :multiple="props.multiple"
          :checkboxes="props.checkboxes"
          :required="props.required"
          :selected-prepend-icon="props.selectedPrependIcon"
          :selected-prepend-icon-fill-color="props.selectedPrependIconFillColor"
          :selected-icon-position="props.selectedIconPosition"
          :hide-no-data="props.hideNoData"
          :hide-search-bar="effectiveHideSearchBar"
          :focus-search-on-open="focusSearchInSheet"
          :no-filter="props.noFilter"
          :selectable-branches="props.selectableBranches"
          :branch-toggle-label="props.branchToggleLabel"
          :branch-toggle-test-id="props.branchToggleTestId"
          :readonly="listReadonly"
          :picker-readonly="props.readonly"
          :loading="loading"
          :placeholder="placeholder"
          :wrap="props.wrap"
          :search="props.search"
          :items="props.items"
          :map-results="props.mapResults"
          :max-results="props.maxResults"
          :fuzzy-search="props.fuzzySearch"
          :highlight-first-result="props.highlightFirstResult"
          :highlight-idle-first-result="props.highlightIdleFirstResult"
          :field-commits-highlight="props.fieldCommitsHighlight"
          :section-order="props.sectionOrder"
          :no-substring-fallback="props.noSubstringFallback"
          :exit-start="props.exitStart"
          :load-children="props.loadChildren"
          :initial-path="props.initialPath"
          :accordion-caret-variant="props.accordionCaretVariant"
          @update-selected="handleUpdateSelected"
          @update-search="handleUpdateSearch"
          @update-status="setResultStatus"
          @update-matches="handleMatchesUpdate"
          @update-current-item="handleUpdateCurrentItem"
          @select-item="handleSelectItem"
          @search-enter="handleSearchEnter"
          @exit-start="handleExitStart"
          @update:on-back="setContainerOnBack"
        >
          <!-- The forwarded slot shadows the panel's own fallback, so the text is repeated here. -->
          <template #no-data>
            <slot name="no-data">{{ props.noDataText }}</slot>
          </template>
        </component>
      </NavigationContainerShell>
    </div>
  </component>

  <!-- Outside the wrapper on purpose. Slotted into the menu it would render through the popup or
       sheet, and a live region whose host is hidden when its text changes announces nothing. -->
  <div class="sp-quantum-select__status" role="status">{{ announcedStatus }}</div>
</template>

<script setup>
/**
 * QuantumSelect is an advanced web component designed to elevate the traditional select element into a highly
 * interactive and user-friendly interface.
 * It seamlessly integrates deep nested structure support, section headers, and a dynamic search input,
 * enabling users to effortlessly navigate and filter through complex datasets.
 *
 * @displayName QuantumSelect
 * @group Custom Elements
 * @component sp-quantum-select
 * @slot selection-trailing - Field actions after the clear control, in selected-tags mode.
 */

import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onUnmounted, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { firstResultRow } from "./composables/useQuantumSelectItems/mark-first-result.js";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { t } from "#ce/shared/forms-i18n";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as quantumSelectProps } from "./props.js";
import { useContainerReset } from "./composables/useContainerReset";
import { useQuantumSelectPopupState } from "./composables/useQuantumSelectPopupState.js";
import { useSelectedTags } from "./composables/useSelectedTags.js";
import { useSelectionModel } from "./composables/useSelectionModel.js";
import AccordionContainer from "./components/AccordionContainer.ce.vue";
import DrilldownContainer from "./components/DrilldownContainer.ce.vue";
import FlatContainer from "./components/FlatContainer.ce.vue";
import NavigationContainerShell from "./components/NavigationContainerShell.ce.vue";

const emit = defineEmits([
  "update-matches",
  "update-selected",
  "update-search",
  "update-open",
  "update-current-item",
  "update-selected-item",
  "search-enter",
  "exit-start",
]);

const props = defineProps(quantumSelectProps);
const attrs = useAttrs();

// sp-quantum-select is a genuine form control: it submits its selection, so it can stand alone in
// a form rather than only inside a host that owns the value.
// `name` is the host's own native member rather than a declared prop, so Vue must not treat it as
// template fallthrough — this root is a fragment, which has nowhere to put an inherited attribute.
defineOptions(formAssociatedHost({ inheritAttrs: false }));

// Disabled is governed by the form-control composable: presence-based own attribute plus ancestor
// <fieldset disabled>.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// A disabled picker holds no open intent, so `open` is refused while disabled rather than stored
// and honoured later, and `update-open` never reports an open that no picker showed.
const menuOpen = ref(toBoolean(props.open) && !disabled.value);

const host = useHostElement();

const { containerResetKey, resetWhenClosed } = useContainerReset({
  open: menuOpen,
  disabled,
  inSheet: () => inSheetMode.value,
  startOnRoot: () => props.startOnRoot,
  emit,
});

// Back-button callback surfaced by the active navigation container via
// `update:on-back`; passed through to the shell.
const containerOnBack = ref();

// One region for the whole picker. The drilldown remounts its panel per frame, and a live region
// inserted alongside the content it describes announces nothing, so the text is reported up here
// rather than rendered where it is computed.
const resultStatus = ref("");

function setResultStatus(event) {
  const [status] = event.detail ?? [event];
  resultStatus.value = status;
}

function setContainerOnBack(value) {
  containerOnBack.value = value;
}

function clearContainerOnBack() {
  containerOnBack.value = undefined;
}

function syncMenuOpenFromProp(value) {
  menuOpen.value = toBoolean(value) && !disabled.value;
}

function handleMenuOpenChange(value) {
  emit("update-open", value);

  if (value && focusSearchInSheet.value) {
    nextTick(focusSearchBar);
  }
}

function focusSearchBar() {
  host?.shadowRoot?.querySelector("sp-search-box")?.inputElement?.focus({ preventScroll: true });
}

watch(() => props.open, syncMenuOpenFromProp);
function closeWhenDisabled(value) {
  if (value) {
    menuOpen.value = false;
  }
}

watch(disabled, closeWhenDisabled);
watch(menuOpen, handleMenuOpenChange);

const listenOnKeyEvent = computed(() => toBoolean(props.listenOnKeyEvent) || menuOpen.value);

const noMenu = computed(() => toBoolean(props.noMenu));
const permanent = computed(() => toBoolean(props.permanent));
// The inline modes always show the list; the menu mode shows it while open.
const listShown = computed(() => noMenu.value || permanent.value || menuOpen.value);

// A screen reader echoes each typed key and drops a status that changes underneath the echo, so a
// status that follows typing is held until the typing has paused. The hold covers a picker that the
// first typed key opens, so the count from before the query is never announced under that key. A
// status the typing outdated is emptied at once rather than left standing, so the region never names
// a row Enter no longer commits; emptying a live region announces nothing.
// Typing reaches the picker from its own search bar as `update-search`, and from a field slotted as
// the activator as `input`. A close empties the region at once: a live region only announces a
// change, so a picker reopened on an unchanged status has to have been emptied in between.
const STATUS_DELAY_AFTER_TYPING_MS = 1000;
const typing = ref(false);
const announcedStatus = ref("");
let typingTimer;

function endTyping() {
  typing.value = false;
}

function noteTyping() {
  typing.value = true;
  clearTimeout(typingTimer);
  typingTimer = setTimeout(endTyping, STATUS_DELAY_AFTER_TYPING_MS);
}

function settleStatus() {
  if (!listShown.value) {
    announcedStatus.value = "";
  } else if (!typing.value) {
    announcedStatus.value = resultStatus.value;
  } else if (announcedStatus.value !== resultStatus.value) {
    announcedStatus.value = "";
  }
}

function stopTypingTimer() {
  clearTimeout(typingTimer);
}

watch([resultStatus, typing, listShown], settleStatus, { flush: "sync", immediate: true });
useEventListener(host, "input", noteTyping);
onUnmounted(stopTypingTimer);

const wrapperComponent = computed(() => (noMenu.value ? "div" : "sp-menu"));

const { isGtSmScreen } = useBreakpoints();

// `no-sheet` applies only to a menu that opens and closes. The permanent and no-menu modes have no sheet.
const keepsPopup = computed(() => !noMenu.value && !permanent.value && toBoolean(props.noSheet));

// True exactly when the wrapper renders as a bottom sheet — mirrors SpMenu's
// own `showBottomSheet` (only the sp-menu wrapper has a sheet, so `noMenu` is
// excluded too).
const inSheetMode = computed(
  () => !noMenu.value && !permanent.value && !isGtSmScreen.value && !toBoolean(props.noSheet),
);

// Effective search-bar visibility passed to the container. `hideSearchBar`
// normally hides it everywhere, but a host that hides it because a slotted
// activator doubles as the search input can opt back in for sheet mode via
// `showSearchBarInSheet`: there the activator is covered by the sheet, so the
// user needs the in-list search bar to filter.
const effectiveHideSearchBar = computed(() => {
  if (!toBoolean(props.hideSearchBar)) {
    return false;
  }
  return !(toBoolean(props.showSearchBarInSheet) && inSheetMode.value);
});

// The sheet covers the slotted activator the user was typing in, so the
// in-list search bar it re-shows is where the typing continues.
const focusSearchInSheet = computed(
  () => inSheetMode.value && toBoolean(props.hideSearchBar) && toBoolean(props.showSearchBarInSheet),
);

// Focuses the search bar under `focus-search-on-open`. `sp-menu` emits `open` only for the desktop
// popup, once it is visible, so the bottom sheet never reaches this handler.
function handleMenuShown() {
  if (toBoolean(props.focusSearchOnOpen) && menuOpen.value) {
    focusSearchBar();
  }
}

const containers = { drilldown: DrilldownContainer, flat: FlatContainer, accordion: AccordionContainer };
// Fall back to the drilldown container if `navigation` is an unsupported value:
// the prop validator only warns in dev, so without this `<component :is>` would
// receive `undefined` and render nothing in production.
const containerComponent = computed(() => containers[props.navigation] ?? DrilldownContainer);

// Switching navigation at runtime swaps the container; the new one is not
// required to emit `update:on-back`, so the previous callback would linger
// and leave a dead back button on the shell.
watch(containerComponent, clearContainerOnBack);
const computedMaxHeight = computed(() => {
  if (permanent.value && !props.maxHeight) {
    return "100%";
  }
  return props.maxHeight;
});

function handleMenuUpdateOpen({ detail }) {
  const [value] = detail;
  menuOpen.value = value;
}

const { getItemValue, getItemTitle, findItem } = useSpList(props);

function closeMenuOnSelect() {
  if (toBoolean(props.closeOnSelect)) {
    menuOpen.value = false;
  }
}

const { model, selectByUser } = useSelectionModel(props, emit, { getItemValue, onChange: closeMenuOnSelect });

// The `selected` prop seeds the selection and names what a form reset restores; `value` is the
// live selection, in the shape `update-selected` carries.
const defaultSelected = toArray(props.selected);

// `toArray(undefined)` yields a one-entry array, so an unselected picker still has a member —
// the entries are what "selected" means here, not the array length.
const selectedValues = computed(() => model.value.map(getItemValue));
const selectedEntries = computed(() => selectedValues.value.filter((entry) => entry !== null && entry !== undefined));

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => selectedValues.value,
    set: (value) => {
      model.value = toArray(isNoSelection(value) ? [] : value);
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultSelected,
  },
  // Read-only: whether the picker presents as a modal bottom sheet.
  inSheetMode: {
    configurable: true,
    get: () => inSheetMode.value,
  },
});

// The list lives in the shadow root, where the outer form cannot see it, so the element submits
// through ElementInternals — one entry per selection, the way a native <select multiple> does.
const { mirrorFormValue, mirrorValueMissing, onFormReset, name: formName } = useForm();

const formValue = computed(() => {
  if (!formName.value) {
    return null;
  }

  const data = new FormData();
  selectedEntries.value.forEach((entry) => data.append(formName.value, String(entry)));

  return data;
});

mirrorFormValue(formValue);

function restoreDefaultSelection() {
  model.value = toArray(defaultSelected);
}

onFormReset(restoreDefaultSelection);

const classModifiers = computed(() => ({ "--disabled": disabled.value }));

// Null so the plain div wrapper of a `no-menu` picker carries no `disabled` attribute.
const menuDisabled = computed(() => (noMenu.value ? null : disabled.value || null));

// A disabled picker's rows must not be selectable, which is what sp-list's `readonly` gates.
const listReadonly = computed(() => toBoolean(props.readonly) || disabled.value);

// A required picker is satisfied only once something is selected.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("select_value_missing"));

mirrorValueMissing(() => isRequired.value && selectedEntries.value.length === 0, validationMessage);

function handleUpdateSelected(value) {
  // A disabled picker must not mutate form state, even through a container whose items stay
  // clickable.
  if (disabled.value) {
    return;
  }

  selectByUser(value);
}

function handleUpdateSearch(value) {
  noteTyping();
  emit("update-search", value);
}

function handleMatchesUpdate(hasMatch) {
  emit("update-matches", hasMatch);
}

function handleUpdateCurrentItem(item) {
  emit("update-current-item", item);
}

// A terminal (leaf) item was selected. Surface the complete item object so
// consumers can route on the full payload (e.g. url / filters / value) — the
// component itself stays agnostic of what those fields mean. `update-selected`
// (value only) continues to fire independently for the simple binding case.
function handleSelectItem(item) {
  emit("update-selected-item", item);
}

// Enter in the panel's search box, surfaced so the host can commit its highlighted row.
function handleSearchEnter(event) {
  emit("search-enter", event);
}

// Arrow Up left the list past its first row (`exit-start` mode), surfaced so
// the host can take focus back.
function handleExitStart() {
  emit("exit-start");
}

// The activator is whatever the consumer slots, so the picker publishes the popup's state onto it
// rather than rendering a control of its own.
const activatorSlot = useTemplateRef("activatorSlot");
const selectionTrailingSlot = useTemplateRef("selectionTrailingSlot");
const {
  tags,
  selectedTagsEnabled,
  hasSelectionActions,
  hasSelectionControls,
  canRemove,
  canClear,
  showClear,
  clearLabel,
  fieldModifiers,
  clearSelection,
} = useSelectedTags(props, {
  model,
  disabled,
  selectByUser,
  getItemValue,
  getItemTitle,
  findItem,
  host,
  slot: activatorSlot,
  announcedStatus,
  trailingSlot: selectionTrailingSlot,
});
useQuantumSelectPopupState({
  readonly: () => props.readonly,
  open: menuOpen,
  slot: activatorSlot,
  enabled: () => !noMenu.value && !permanent.value,
});

// Moves roving focus to the list's first / last option, or under `preferSelected` to its first
// selected one. No-op when no list is rendered.
const { exposeMethods } = useExpose();
exposeMethods({
  focusListItem(location, options) {
    host?.shadowRoot?.querySelector("sp-list")?.focusItem?.(location, options);
  },
  firstResult,
  // Commits `firstResult()` through the list's own user path, so it reports like a click. Returns
  // the item only when the list newly selected it: null while the picker is closed or disabled, on
  // a readonly list, for a row already selected, or with nothing committable. `markedOnly` commits
  // the highlighted row alone.
  selectFirstResult({ markedOnly = false } = {}) {
    if (disabled.value || !listShown.value) {
      return null;
    }

    const item = firstResult({ markedOnly });
    const committed = item != null && host?.shadowRoot?.querySelector("sp-list")?.selectItem?.(item);

    return committed ? item : null;
  },
});

// The rendered list's first result, read the same way `focusListItem` reaches the list. The row
// the highlight marked is honoured only when this picker's own prop produced the mark, so a
// consumer hand-marking rows for visuals does not steer what Enter commits.
function firstResult({ markedOnly = false } = {}) {
  const items = host?.shadowRoot?.querySelector("sp-list")?.items ?? [];

  return firstResultRow(items, { honourMarked: toBoolean(props.highlightFirstResult), markedOnly });
}
</script>

<style lang="scss">
@use "sass:map";

/* Each `--sp-comp-quantum-select-*` popup token sets the menu token it names, and each item token
   the list-item token it names. An unset one leaves the value a page sets on the host for that
   inner token, then the inner default. */
$menu-tokens: (
  "popup-offset": "menu-offset",
  "popup-offset-inline": "menu-offset-inline",
  "popup-pointer-size": "menu-pointer-size",
  "popup-pointer-offset": "menu-pointer-offset",
  "popup-background-color": "menu-background-color",
  "popup-box-shadow": "menu-box-shadow",
  "popup-border-radius": (
    "menu-border-radius",
    "menu-content-border-radius",
  ),
);
$item-tokens: (
  "item-min-height": "list-item-min-height",
  "item-padding": "list-item-padding",
  "item-margin-inline": "list-item-margin-inline",
  "item-box-sizing": "list-item-box-sizing",
  "item-content-min-height": "list-item-content-min-height",
  "item-divider-inset": "list-item-divider-inset",
  "item-divider-display": "list-item-display-divider",
  "item-border-bottom": "list-item-border-bottom",
  "item-padding-block": "list-item-padding-block",
  "item-padding-inline": "list-item-padding-inline",
  "item-align-items": "list-item-align-items",
  "item-color": "list-item-color",
  "item-font": "list-item-content-font",
  "item-line-height": "list-item-line-height",
  "item-hover-background-color": "list-item-hover-background-color",
  "item-hover-color": "list-item-hover-color",
  "item-focused-background-color": "list-item-focused-background-color",
  "item-focus-box-shadow": "list-item-focus-box-shadow",
  "item-selected-background-color": "list-item-active-background-color",
  "item-selected-hover-background-color": "list-item-active-hover-background-color",
  "item-selected-color": "list-item-active-color",
  "item-selected-font": "list-item-active-content-font",
  "item-icon-offset": "list-item-prepend-margin-left",
  "item-icon-width": "list-item-prepend-min-width",
  "item-icon-gap": "list-item-prepend-margin-right",
  "item-selected-icon-size": "list-item-active-icon-size",
  "subheader-background-color": "list-subheader-background-color",
  "subheader-color": "list-subheader-color",
  "subheader-font-family": "list-subheader-font-family",
  "subheader-font-size": "list-subheader-font-size",
  "subheader-font-weight": "list-subheader-font-weight",
  "subheader-min-height": "list-subheader-min-height",
  "subheader-padding-block": "list-subheader-padding-block",
  "subheader-padding-inline": "list-subheader-padding-inline",
);

:host {
  @each $token, $inners in map.merge($menu-tokens, $item-tokens) {
    @each $inner in $inners {
      --quantum-host-#{$inner}: var(--sp-comp-#{$inner});
    }
  }

  display: block;
  --quantum-select-content-padding: var(--sp-comp-quantum-select-content-padding, 0.75rem);
  --quantum-select-content-padding-top: var(--sp-comp-quantum-select-content-padding-top, 0.5rem);
  --quantum-select-content-padding-bottom: var(--sp-comp-quantum-select-content-padding-bottom, 0.5rem);
  --quantum-select-popup-min-height: var(
    --sp-comp-quantum-select-popup-min-height,
    var(--sp-comp-popup-min-height, 25rem)
  );
  --quantum-select-popup-max-height: var(
    --sp-comp-quantum-select-popup-max-height,
    var(--quantum-select-popup-min-height)
  );

  --default-select-popup-width: 18.5rem;
  --quantum-select-popup-width: var(--sp-comp-quantum-select-popup-width, var(--default-select-popup-width));
  --quantum-select-animation-time: var(--sp-comp-quantum-select-animation-time, 0.3s);

  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
}

:host([no-menu]) {
  --default-select-popup-width: 100%;
  height: 100%;
}

:host([permanent]) {
  height: 100%;
}

/*
 * Only the full-height inline modes (permanent / no-menu, e.g. the required
 * filters modal) should stretch the inner wrapper to fill the host. In the
 * default menu/activator mode the wrapper must size to its content so the
 * activator can be vertically centered by the parent layout.
 */
:host([permanent]) .sp-quantum-select,
:host([no-menu]) .sp-quantum-select {
  height: 100%;
}

.sp-quantum-select {
  @each $token, $inners in $menu-tokens {
    @each $inner in $inners {
      --sp-comp-#{$inner}: var(--sp-comp-quantum-select-#{$token}, var(--quantum-host-#{$inner}));
    }
  }
}

.sp-quantum-select__content {
  @each $token, $inner in $item-tokens {
    --sp-comp-#{$inner}: var(--sp-comp-quantum-select-#{$token}, var(--quantum-host-#{$inner}));
  }
}

/* `sp-list-item` takes physical prepend margins, so the icon's offset and gap swap sides in a
   right-to-left list. */
.sp-quantum-select__content:dir(rtl) {
  --sp-comp-list-item-prepend-margin-left: var(
    --sp-comp-quantum-select-item-icon-gap,
    var(--quantum-host-list-item-prepend-margin-left)
  );
  --sp-comp-list-item-prepend-margin-right: var(
    --sp-comp-quantum-select-item-icon-offset,
    var(--quantum-host-list-item-prepend-margin-right)
  );
}
</style>

<style lang="scss" scoped>
.sp-quantum-select {
  height: auto;
}

.sp-quantum-select__activator.--selection-controls {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--sp-comp-quantum-select-selected-tags-gap, var(--sp-ref-scale-4));
  position: relative;
  padding-inline: var(
    --sp-comp-quantum-select-selected-tags-padding-inline,
    var(--sp-comp-quantum-select-selected-tags-padding, var(--sp-ref-scale-4))
  );
  padding-block: var(
    --sp-comp-quantum-select-selected-tags-padding-block,
    var(--sp-comp-quantum-select-selected-tags-padding, var(--sp-ref-scale-4))
  );
  min-height: var(--sp-comp-quantum-select-selected-tags-min-height, var(--sp-ref-scale-44));
  box-sizing: border-box;
  border: var(--sp-comp-quantum-select-selected-tags-border-width, var(--sp-sys-border-width-default))
    var(--sp-comp-quantum-select-selected-tags-border-style, solid)
    var(--sp-comp-quantum-select-selected-tags-border-color, var(--sp-sys-color-outline));
  border-radius: var(--sp-comp-quantum-select-selected-tags-border-radius, var(--sp-sys-shape-control));
  background: var(--sp-comp-quantum-select-selected-tags-background-color, var(--sp-sys-color-surface));
  outline: none;
  outline-offset: var(--sp-comp-quantum-select-selected-tags-outline-offset, 0);

  &::before {
    content: "";
    position: absolute;
    inset: calc(-1 * var(--sp-comp-quantum-select-selected-tags-border-width, var(--sp-sys-border-width-default)));
    border-radius: inherit;
    pointer-events: none;
    box-shadow: var(--sp-comp-quantum-select-selected-tags-box-shadow, none);
  }

  &.--disabled {
    opacity: var(--sp-comp-quantum-select-selected-tags-disabled-opacity, 1);
    cursor: var(--sp-comp-quantum-select-selected-tags-disabled-cursor, not-allowed);
  }
}
.sp-quantum-select__activator.--selection-controls:not(.--disabled):focus-within {
  border-color: var(
    --sp-comp-quantum-select-selected-tags-focus-border-color,
    var(--sp-comp-quantum-select-selected-tags-border-color, var(--sp-sys-color-outline))
  );
  box-shadow: var(--sp-comp-quantum-select-selected-tags-focus-box-shadow, none);
  outline: var(
    --sp-comp-quantum-select-selected-tags-focus-outline,
    var(--sp-sys-state-focus-indicator-thickness) solid var(--sp-sys-color-primary)
  );
  outline-offset: var(
    --sp-comp-quantum-select-selected-tags-focus-outline-offset,
    var(--sp-sys-state-focus-indicator-outer-offset)
  );
}
.sp-quantum-select__selected-content {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: var(--sp-comp-quantum-select-selected-tags-gap, var(--sp-ref-scale-4));
  padding-block: var(--sp-comp-quantum-select-selected-tags-content-padding-block, var(--sp-ref-scale-4));
}
.sp-quantum-select__selected-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--sp-comp-quantum-select-selected-tags-actions-gap, var(--sp-ref-scale-4));
}
.sp-quantum-select__selected-input {
  flex: 1 1 var(--sp-comp-quantum-select-selected-tags-input-basis, var(--sp-ref-scale-128));
  min-width: 0;
}
.sp-quantum-select__tag {
  --sp-comp-chip-content-padding-block: var(
    --sp-comp-quantum-select-selected-tags-chip-padding-block,
    var(--sp-ref-scale-2)
  );
  --sp-comp-chip-color: var(
    --sp-comp-quantum-select-selected-tags-color,
    var(--sp-comp-quantum-select-chip-color, var(--sp-sys-color-on-surface))
  );
  --sp-comp-chip-font-family: var(
    --sp-comp-quantum-select-selected-tags-font-family,
    var(--sp-comp-quantum-select-chip-font-family, var(--sp-sys-typescale-body-font-family))
  );
  --sp-comp-chip-label-font-size: var(
    --sp-comp-quantum-select-selected-tags-font-size,
    var(--sp-comp-quantum-select-chip-font-size, var(--sp-sys-typescale-body-font-size))
  );
  --sp-comp-chip-font-weight: var(
    --sp-comp-quantum-select-selected-tags-font-weight,
    var(--sp-comp-quantum-select-chip-font-weight, var(--sp-sys-typescale-body-font-weight))
  );
  --sp-comp-chip-line-height: var(
    --sp-comp-quantum-select-selected-tags-line-height,
    var(--sp-comp-quantum-select-chip-line-height, var(--sp-sys-typescale-body-line-height))
  );
  --sp-comp-chip-letter-spacing: var(
    --sp-comp-quantum-select-selected-tags-letter-spacing,
    var(--sp-comp-quantum-select-chip-letter-spacing, inherit)
  );
}
.sp-quantum-select__activator.--selection-controls .sp-quantum-select__clear {
  --sp-comp-button-small-height: var(
    --sp-comp-quantum-select-selected-tags-clear-button-height,
    var(--sp-ref-scale-32)
  );
}
.sp-quantum-select__content {
  position: relative;
  height: 100%;
}
.sp-quantum-select__status {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
